import { ref, readonly } from "vue";

type ComponentFlag = {
  name: string;
  enabled: boolean;
  category: "dtp" | "ist";
};

// Define all DTP components
const dtpComponents = [
  "dtpBadge",
  "dtpBanner",
  "dtpBtn",
  "dtpCard",
  "dtpCardActions",
  "dtpCardSection",
  "dtpCheckbox",
  "dtpChip",
  "dtpIcon",
  "dtpItem",
  "dtpItemLabel",
  "dtpItemSection",
  "dtpList",
  "dtpButtonDropdown",
  "dtpPageSticky",
  "dtpRadio",
  "dtpSplitter",
  "dtpTabs",
  "dtpToggle",
  "dtpToolbar",
];

// Define all IST components
const istComponents = [
  "IstAvatar",
  "IstChipSelect",
  "IstDatePicker",
  "IstFormDateTime",
  "IstFormInput",
  "IstFormSearch",
  "IstFormSelect",
  "IstFormTime",
  "IstFormTimeSmall",
  "IstFormValidationError",
  "IstInfoCenter",
  "IstInfoCenterItem",
  "IstLayoutCard",
  "IstLayoutHeader",
  "IstLayoutLeftMenu",
  "IstMultipleSelect",
  "IstNumberInput",
  "IstToolbarDate",
  "IstToolbarWeek",
];

const STORAGE_KEY = "featureFlags";

// Initialize flags from localStorage or default to enabled
const initializeFlags = (): ComponentFlag[] => {
  // Default: all components enabled
  const dtpFlags: ComponentFlag[] = dtpComponents.map((name) => ({
    name,
    enabled: true,
    category: "dtp" as const,
  }));

  const istFlags: ComponentFlag[] = istComponents.map((name) => ({
    name,
    enabled: true,
    category: "ist" as const,
  }));

  const defaultFlags = [...dtpFlags, ...istFlags];

  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    try {
      const storedFlags = JSON.parse(stored);

      // Merge stored flags with default flags to handle newly added components
      const mergedFlags = defaultFlags.map((defaultFlag) => {
        const storedFlag = storedFlags.find(
          (f: ComponentFlag) => f.name === defaultFlag.name,
        );
        return storedFlag || defaultFlag;
      });

      return mergedFlags;
    } catch {
      // If parse fails, return default
      return defaultFlags;
    }
  }

  return defaultFlags;
};

const featureFlags = ref<ComponentFlag[]>(initializeFlags());
const dialogOpen = ref(false);

// Save to localStorage whenever flags change
const saveFlags = () => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(featureFlags.value));
};

export const useFeatureFlags = () => {
  const toggleFlag = (componentName: string) => {
    const flag = featureFlags.value.find((f) => f.name === componentName);
    if (flag) {
      flag.enabled = !flag.enabled;
      saveFlags();
    }
  };

  const setFlag = (componentName: string, enabled: boolean) => {
    const flag = featureFlags.value.find((f) => f.name === componentName);
    if (flag) {
      flag.enabled = enabled;
      saveFlags();
    }
  };

  const isEnabled = (componentName: string): boolean => {
    const flag = featureFlags.value.find((f) => f.name === componentName);
    return flag?.enabled ?? true;
  };

  const enableAll = () => {
    featureFlags.value.forEach((flag) => {
      flag.enabled = true;
    });
    saveFlags();
  };

  const disableAll = () => {
    featureFlags.value.forEach((flag) => {
      flag.enabled = false;
    });
    saveFlags();
  };

  const enableCategory = (category: "dtp" | "ist") => {
    featureFlags.value
      .filter((flag) => flag.category === category)
      .forEach((flag) => {
        flag.enabled = true;
      });
    saveFlags();
  };

  const disableCategory = (category: "dtp" | "ist") => {
    featureFlags.value
      .filter((flag) => flag.category === category)
      .forEach((flag) => {
        flag.enabled = false;
      });
    saveFlags();
  };

  const openDialog = () => {
    dialogOpen.value = true;
  };

  const closeDialog = () => {
    dialogOpen.value = false;
  };

  return {
    flags: readonly(featureFlags),
    dialogOpen: readonly(dialogOpen),
    toggleFlag,
    setFlag,
    isEnabled,
    enableAll,
    disableAll,
    enableCategory,
    disableCategory,
    openDialog,
    closeDialog,
  };
};
